import { FaExclamationTriangle, FaMoneyBillWave, FaSadTear, FaPhoneSlash, FaBalanceScale, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { HiSparkles, HiLightningBolt } from 'react-icons/hi';
import { BsExclamationTriangleFill, BsShieldFillExclamation } from 'react-icons/bs';
import { IoMdRocket } from 'react-icons/io';

function ProblemsSection() {
  const problems = [
    { 
      icon: FaMoneyBillWave, 
      title: "Payment Stuck?", 
      desc: "Struggling to collect payments from defaulters?",
      gradient: "from-red-500 to-rose-500",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      iconBg: "bg-red-100",
      iconColor: "text-red-500"
    },
    { 
      icon: FaSadTear, 
      title: "Lost Confidence?", 
      desc: "Feeling helpless when customers give excuses?",
      gradient: "from-orange-500 to-amber-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-500"
    },
    { 
      icon: FaPhoneSlash, 
      title: "Awkward Calls?", 
      desc: "Don't know what to say when following up?",
      gradient: "from-purple-500 to-violet-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-500"
    },
    { 
      icon: FaBalanceScale, 
      title: "Legal Confusion?", 
      desc: "Unsure about your rights & legal actions?",
      gradient: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-500"
    }
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 rounded-3xl p-8 sm:p-12 mb-10">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-red-200/30 to-orange-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-amber-200/30 to-red-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      
      {/* Warning Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_10px,rgba(239,68,68,0.1)_10px,rgba(239,68,68,0.1)_20px)]"></div>
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Alert Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-rose-500 text-white px-5 py-2 rounded-full text-sm font-semibold mb-6 shadow-lg shadow-red-500/30 animate-pulse">
            <BsExclamationTriangleFill />
            <span>Attention Business Owners!</span>
            <BsShieldFillExclamation />
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Are You Facing
            <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent"> These Problems?</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            You're not alone. <span className="font-semibold text-red-600">Thousands of business owners</span> face these challenges daily. But there's a solution!
          </p>
        </div>
        
        {/* Problems Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {problems.map((problem, index) => (
            <div 
              key={index}
              className={`group relative ${problem.bgColor} rounded-2xl p-6 text-center border-2 ${problem.borderColor} hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden cursor-pointer`}
            >
              {/* Hover Gradient Overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${problem.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Number Badge */}
              <div className="absolute top-3 right-3 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center text-sm font-bold text-gray-400 group-hover:bg-white/20 group-hover:text-white transition-all">
                {index + 1}
              </div>
              
              {/* Icon */}
              <div className={`relative w-16 h-16 mx-auto mb-4 ${problem.iconBg} rounded-2xl flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                <problem.icon className={`text-3xl ${problem.iconColor} group-hover:text-white transition-colors`} />
              </div>
              
              {/* Title */}
              <h3 className="relative text-xl font-bold text-gray-800 mb-2 group-hover:text-white transition-colors">
                {problem.title}
              </h3>
              
              {/* Description */}
              <p className="relative text-gray-600 text-sm group-hover:text-white/90 transition-colors">
                {problem.desc}
              </p>
              
              {/* Bottom Accent */}
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${problem.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
            </div>
          ))}
        </div>
        
        {/* Solution CTA */}
        <div className="relative bg-white rounded-2xl p-8 shadow-xl border border-gray-100 max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Left Content */}
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center md:justify-start gap-2">
                <HiSparkles className="text-amber-500" />
                Good News!
              </h3>
              <p className="text-gray-600">
                These problems have <span className="font-semibold text-green-600">proven solutions</span>. Learn the exact strategies to overcome them.
              </p>
            </div>
            
            {/* Checkmarks */}
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-green-500" />
                No more excuses from defaulters
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-green-500" />
                Confident payment follow-ups
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-600">
                <FaCheckCircle className="text-green-500" />
                Know your legal rights
              </span>
            </div>
            
            {/* CTA Button */}
            
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProblemsSection;
